import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    InputAdornment,
    ListItemText,
    MenuItem,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SensorsOffOutlinedIcon from "@mui/icons-material/SensorsOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import _ from "lodash";
import connectivityAlertServices from "app/services/automations/connectivity-alert-services";
import { errorMessage } from "app/shared/devices/actuatorDisplay";
import AutomationMetaHeader from "../../components/AutomationMetaHeader";
import EditorSaveBar from "../../components/EditorSaveBar";
import RecipientsEditor from "../../components/RecipientsEditor";
import ScheduleEditor from "../../components/ScheduleEditor";
import SectionCard from "../../components/SectionCard";
import { COOLDOWN_OPTIONS, detectSchedulePreset, hasScheduleWindow, humanDuration } from "../alertRule/alertRuleConstants";

export const SCOPE_OPTIONS = [
    { value: "company", label: "Toda la empresa" },
    { value: "stores", label: "Algunos locales" },
    { value: "devices", label: "Algunos equipos" },
];

const DEFAULT_LIMITS = {
    device_after_minutes: { min: 30, max: 1440, default: 60 },
    gateway_after_minutes: { min: 5, max: 1440, default: 15 },
    cooldown_minutes: { min: 5, default: 60 },
};

const EMPTY_FORM = {
    id: null,
    name: "Conexión de equipos",
    description: "",
    notification_group: "",
    enabled: true,
    scope: "company",
    store_ids: [],
    device_ids: [],
    excluded_device_ids: [],
    watch_gateways: true,
    gateway_after_minutes: 15,
    watch_devices: true,
    device_after_minutes: 60,
    cooldown_minutes: 60,
    notify_recovery: true,
    notify_company_admins: false,
    schedule_start_minute: null,
    schedule_end_minute: null,
    schedule_days: [],
    recipients: [],
};

const alertToForm = (alert = {}) => ({
    ...EMPTY_FORM,
    ..._.pick(alert, ["id", "name", "description", "notification_group", "scope", "store_ids", "device_ids", "excluded_device_ids",
        "gateway_after_minutes", "device_after_minutes", "cooldown_minutes", "schedule_start_minute", "schedule_end_minute", "schedule_days"]),
    description: alert.description || "",
    notification_group: alert.notification_group || "",
    enabled: alert.enabled !== false,
    watch_gateways: alert.watch_gateways !== false,
    watch_devices: alert.watch_devices !== false,
    notify_recovery: alert.notify_recovery !== false,
    notify_company_admins: alert.notify_company_admins === true,
    recipients: (alert.recipients || []).map((recipient) => _.pick(recipient, ["id", "user_id", "channel", "enabled", "notify_after_seconds"])),
});

const inRange = (value, { min, max }) => {
    const number = Number(value);
    return value !== "" && Number.isInteger(number) && number >= min && (_.isNil(max) || number <= max);
};

const MinutesField = ({ label, value, onChange, limits, disabled, suffix = "min" }) => {
    const valid = inRange(value, limits);
    return (
        <TextField
            fullWidth
            type="number"
            label={label}
            value={value ?? ""}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            error={!disabled && !valid}
            helperText={!disabled && !valid ? `Entre ${limits.min} y ${limits.max} minutos.` : undefined}
            inputProps={{ min: limits.min, max: limits.max, step: 1 }}
            InputProps={{ endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> }}
        />
    );
};

// Editor de «Conexión de equipos»: qué vigilar (empresa, locales o equipos), qué avisar (local sin conexión y sensor sin
// datos, con sus tiempos), a quién y en qué horario.
const ConnectivityAlertEditor = ({ id, duplicateFromId, groupOptions = [], onSaved, onCancel, setAssistantContext }) => {
    const [options, setOptions] = useState(null);
    const [form, setForm] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    // Al guardar se vuelve a la lista: el editor ya no existe cuando termina la petición.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    useEffect(() => {
        let active = true;
        const sourceId = id || duplicateFromId;
        Promise.all([
            connectivityAlertServices.getEditOptions(),
            sourceId ? connectivityAlertServices.getConnectivityAlert(sourceId) : Promise.resolve(null),
        ])
            .then(([editOptions, alert]) => {
                if (!active) return;
                setOptions(editOptions || {});
                if (id && alert) setForm(alertToForm(alert));
                else if (duplicateFromId && alert) setForm({ ...alertToForm(alert), id: null, name: `Copia de ${alert.name}`, enabled: false });
                else setForm({ ...EMPTY_FORM });
            })
            .catch(() => { if (active) setLoadError("No se pudo cargar la automatización."); });
        return () => { active = false; };
    }, [duplicateFromId, id]);

    useEffect(() => {
        if (!form) return;
        setAssistantContext?.({ mode: "form", current_connectivity_alert: _.pick(form, ["id", "name", "scope", "enabled"]) });
    }, [form, setAssistantContext]);

    const limits = { ...DEFAULT_LIMITS, ...(options?.limits || {}) };
    const stores = useMemo(() => options?.stores || [], [options]);
    const devices = useMemo(() => options?.devices || [], [options]);
    const update = (patch) => setForm((current) => ({ ...current, ...patch }));

    const pick = (items, ids) => _.filter(items, (item) => _.includes(ids, item.id));
    const scopeDevices = useMemo(() => {
        if (!form) return [];
        if (form.scope === "stores") return devices.filter((device) => _.includes(form.store_ids, device.store_id));
        if (form.scope === "devices") return pick(devices, form.device_ids);
        return devices;
    }, [devices, form]);

    if (loadError) return <Alert severity="error">{loadError}</Alert>;
    if (!form || !options) {
        return (
            <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const cooldownSeconds = Number(form.cooldown_minutes) * 60;
    const cooldownOptions = _.some(COOLDOWN_OPTIONS, { value: cooldownSeconds })
        ? COOLDOWN_OPTIONS
        : _.sortBy([...COOLDOWN_OPTIONS, { value: cooldownSeconds, label: humanDuration(cooldownSeconds) }], "value");
    const recipientsReady = _.every(form.recipients, (recipient) => recipient.user_id && Number.isInteger(recipient.notify_after_seconds ?? 0));
    const someoneNotified = form.notify_company_admins || _.some(form.recipients, (recipient) => recipient.user_id && recipient.enabled !== false);
    const scheduleReady = !(hasScheduleWindow(form) && form.schedule_start_minute === form.schedule_end_minute)
        && !(detectSchedulePreset(form) === "custom" && !(form.schedule_days || []).length);
    const canSave = !!form.name?.trim()
        && (form.watch_gateways || form.watch_devices)
        && (!form.watch_gateways || inRange(form.gateway_after_minutes, limits.gateway_after_minutes))
        && (!form.watch_devices || inRange(form.device_after_minutes, limits.device_after_minutes))
        && (form.scope !== "stores" || form.store_ids.length > 0)
        && (form.scope !== "devices" || form.device_ids.length > 0)
        && recipientsReady && someoneNotified && scheduleReady;

    const save = () => {
        if (!canSave) return;
        const payload = {
            ..._.pick(form, ["scope", "store_ids", "device_ids", "excluded_device_ids", "watch_gateways", "watch_devices", "notify_recovery",
                "notify_company_admins", "schedule_days"]),
            name: form.name.trim(),
            description: form.description || "",
            notification_group: form.notification_group?.trim() || null,
            enabled: form.enabled !== false,
            gateway_after_minutes: Number(form.gateway_after_minutes),
            device_after_minutes: Number(form.device_after_minutes),
            cooldown_minutes: Number(form.cooldown_minutes),
            schedule_start_minute: hasScheduleWindow(form) ? form.schedule_start_minute : null,
            schedule_end_minute: hasScheduleWindow(form) ? form.schedule_end_minute : null,
            recipients: form.recipients.filter((recipient) => recipient.user_id).map((recipient) => ({
                user_id: recipient.user_id,
                channel: recipient.channel,
                enabled: recipient.enabled !== false,
                notify_after_minutes: Math.round((recipient.notify_after_seconds || 0) / 60),
            })),
        };

        setSaving(true);
        setSaveError(null);
        const request = form.id
            ? connectivityAlertServices.updateConnectivityAlert(form.id, payload)
            : connectivityAlertServices.createConnectivityAlert(payload);
        request
            .then((saved) => onSaved?.(saved))
            .catch((error) => { if (mountedRef.current) setSaveError(errorMessage(error, "No se pudo guardar la automatización.")); })
            .finally(() => { if (mountedRef.current) setSaving(false); });
    };

    const multiSelect = ({ label, items, ids, onChange, secondary, placeholder, helperText }) => (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={items}
            value={pick(items, ids)}
            onChange={(_event, selected) => onChange(_.map(selected, "id"))}
            getOptionLabel={(item) => item?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, item, { selected }) => (
                <li {...props} key={`${label}-${item.id}`}>
                    <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                    <ListItemText primary={item.name} secondary={secondary?.(item)} />
                </li>
            )}
            renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} helperText={helperText} />}
        />
    );

    const notReported = scopeDevices.filter((device) => !device.reported).length;

    return (
        <>
            <Stack spacing={2.25}>
                <AutomationMetaHeader
                    automation={form}
                    onChange={update}
                    onBack={onCancel}
                    groupOptions={groupOptions}
                    controls={(
                        <TextField
                            select
                            size="small"
                            label="Repetir el aviso cada"
                            value={`${cooldownSeconds}`}
                            onChange={(event) => update({ cooldown_minutes: Number(event.target.value) / 60 })}
                            sx={{ minWidth: 172 }}>
                            {cooldownOptions.map((option) => (
                                <MenuItem key={`cooldown-${option.value}`} value={`${option.value}`}>{option.label}</MenuItem>
                            ))}
                        </TextField>
                    )}
                />

                <SectionCard
                    title="Qué vigilar"
                    subtitle={form.scope === "devices"
                        ? "Solo los equipos que elijas."
                        : "Los equipos que se instalen después quedan incluidos solos."}
                    icon={<VisibilityOutlinedIcon />}
                    accent="primary.main">
                    <Stack spacing={1.5}>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={form.scope}
                            onChange={(_event, scope) => scope && update({ scope })}
                            sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { px: 1.5, textTransform: "none" } }}>
                            {SCOPE_OPTIONS.map((option) => <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>)}
                        </ToggleButtonGroup>
                        {form.scope === "stores" && multiSelect({
                            label: "Locales",
                            items: stores,
                            ids: form.store_ids,
                            onChange: (storeIds) => update({ store_ids: storeIds }),
                            secondary: (store) => (store.gateways ? `${store.gateways} gateway${store.gateways === 1 ? "" : "s"}` : "Sin gateway registrado"),
                            placeholder: "Agregar local",
                        })}
                        {form.scope === "devices" && multiSelect({
                            label: "Equipos",
                            items: devices,
                            ids: form.device_ids,
                            onChange: (deviceIds) => update({ device_ids: deviceIds }),
                            secondary: (device) => device.store_name,
                            placeholder: "Agregar equipo",
                        })}
                        {form.scope !== "devices" && multiSelect({
                            label: "Excluir equipos (opcional)",
                            items: scopeDevices,
                            ids: form.excluded_device_ids,
                            onChange: (deviceIds) => update({ excluded_device_ids: deviceIds }),
                            secondary: (device) => device.store_name,
                            placeholder: "Por ejemplo, uno que se apaga por temporada",
                        })}
                        {notReported > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                {notReported === 1 ? "1 equipo todavía no ha reportado" : `${notReported} equipos todavía no han reportado`}: no avisa por ellos hasta su primera lectura.
                            </Typography>
                        )}
                    </Stack>
                </SectionCard>

                <SectionCard
                    title="Qué avisar"
                    subtitle="Lo que ya estaba caído al crear la automatización no se avisa: solo lo que se caiga desde ahora."
                    icon={<SensorsOffOutlinedIcon />}
                    accent="error.main">
                    <Stack spacing={2}>
                        <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <FormControlLabel
                                    sx={{ ml: 0 }}
                                    control={<Switch checked={form.watch_gateways} onChange={(event) => update({ watch_gateways: event.target.checked })} />}
                                    label="Local sin conexión"
                                />
                                <Typography variant="caption" color="text.secondary" component="p">
                                    Un solo aviso cuando se cae el gateway o el equipo de IA del local. Mientras tanto no se avisa por cada sensor.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <MinutesField
                                    label="Después de"
                                    value={form.gateway_after_minutes}
                                    onChange={(value) => update({ gateway_after_minutes: value })}
                                    limits={limits.gateway_after_minutes}
                                    disabled={!form.watch_gateways}
                                    suffix="min sin conexión"
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <FormControlLabel
                                    sx={{ ml: 0 }}
                                    control={<Switch checked={form.watch_devices} onChange={(event) => update({ watch_devices: event.target.checked })} />}
                                    label="Sensor sin datos"
                                />
                                <Typography variant="caption" color="text.secondary" component="p">
                                    El aviso dice su último valor y su batería, para saber por qué se calló.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <MinutesField
                                    label="Después de"
                                    value={form.device_after_minutes}
                                    onChange={(value) => update({ device_after_minutes: value })}
                                    limits={limits.device_after_minutes}
                                    disabled={!form.watch_devices}
                                    suffix="min sin datos"
                                />
                            </Grid>
                        </Grid>
                        {!form.watch_gateways && !form.watch_devices && (
                            <Alert severity="warning">Elige al menos una de las dos.</Alert>
                        )}
                    </Stack>
                </SectionCard>

                <SectionCard
                    title="A quién avisar"
                    subtitle="Cada persona recibe el primer aviso cuando se cumple su tiempo y después se le repite mientras siga."
                    icon={<NotificationsActiveOutlinedIcon />}
                    accent="primary.main">
                    <Stack spacing={1.25}>
                        <FormControlLabel
                            sx={{ ml: 0 }}
                            control={<Checkbox checked={form.notify_company_admins} onChange={(event) => update({ notify_company_admins: event.target.checked })} />}
                            label="Avisar a los administradores de la empresa (por correo)"
                        />
                        <RecipientsEditor
                            recipients={form.recipients}
                            onChange={(recipients) => update({ recipients })}
                            users={options.users || []}
                            channels={options.channels || []}
                            emptyText={form.notify_company_admins ? "Solo los administradores." : "Nadie recibe avisos todavía."}
                            showDelay
                        />
                        <FormControlLabel
                            sx={{ ml: 0 }}
                            control={<Switch checked={form.notify_recovery} onChange={(event) => update({ notify_recovery: event.target.checked })} />}
                            label="Avisar cuando vuelva a enviar datos"
                        />
                    </Stack>
                </SectionCard>

                <SectionCard
                    title="Cuándo notificar"
                    subtitle="Fuera del horario no avisa, pero el tiempo corre: lo atrasado sale al abrirse la ventana."
                    icon={<EventAvailableOutlinedIcon />}
                    accent="success.main">
                    <ScheduleEditor value={form} onChange={update} />
                </SectionCard>

                {!!saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
                {!canSave && (
                    <Alert severity="info">
                        Completa el nombre, qué vigilar, qué avisar con tiempos válidos y al menos a quién avisar para guardar.
                    </Alert>
                )}
            </Stack>
            <EditorSaveBar saving={saving} disabled={!canSave} onSave={save} />
        </>
    );
};

export default ConnectivityAlertEditor;
